import { useState } from 'react';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2 } from 'lucide-react';
import Reveal from '../components/animations/Reveal';
import AuthBackground from '../components/animations/AuthBackground';
import { resetPassword } from '../api/authApi';

const INPUT_CLASS =
  'input-anim w-full bg-white/95 border border-white/40 rounded-xl p-3 pl-11 pr-11 text-slate-800 placeholder-slate-400 focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none transition-all';

// token comes from App.jsx, which read it out of ?resetToken=... on load
const ResetPasswordPage = ({ token, setActiveTab }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Could not reset password. The link may have expired.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex flex-col items-center pt-16 pb-16 px-4">
      <AuthBackground />
      <div className="relative max-w-md w-full mx-auto sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center mb-6">
            <h2 className="font-display text-4xl font-bold text-white mb-3 drop-shadow-sm">Set New Password</h2>
            <p className="text-xl text-white/80">Choose a new password for your account</p>
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 border border-white/20 shadow-2xl">
            {!token ? (
              <div className="text-center py-4">
                <AlertCircle className="w-10 h-10 text-red-300 mx-auto mb-4" />
                <p className="text-white mb-2 font-medium">No reset link detected</p>
                <p className="text-sm text-white/60 mb-6">
                  Open this page using the link from your password reset email.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab?.('forgot-password')}
                  className="text-white hover:underline text-sm"
                >
                  Request a new link
                </button>
              </div>
            ) : done ? (
              <div className="text-center py-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-300 mx-auto mb-4" />
                <p className="text-white mb-2 font-medium">Password updated</p>
                <p className="text-sm text-white/60 mb-6">You can now log in with your new password.</p>
                <button
                  type="button"
                  onClick={() => setActiveTab?.('login')}
                  className="brand-btn px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300"
                >
                  Go to Login
                </button>
              </div>
            ) : (
              <form className="space-y-5" onSubmit={handleSubmit}>
                <div className="relative">
                  <Lock className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="New password (min. 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4.5 h-4.5" /> : <Eye className="w-4.5 h-4.5" />}
                  </button>
                </div>

                <div className="relative">
                  <Lock className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Confirm new password"
                  />
                </div>

                {error && (
                  <div className="flex items-start space-x-3 bg-red-500/10 border border-red-400/30 rounded-2xl p-4">
                    <AlertCircle className="w-5 h-5 text-red-300 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-red-200">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="brand-btn w-full px-6 py-3 rounded-xl font-semibold hover:scale-[1.02] transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Updating...' : 'Update Password'}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default ResetPasswordPage;